import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "overview-contact-block" }
const _hoisted_2 = { class: "overview-contact-block__content" }
const _hoisted_3 = {
  key: 0,
  class: "overview-contact-block__empty"
}
const _hoisted_4 = { class: "overview-contact-block__empty-description typographies-paragraph-medium-regular" }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "overview-contact-block__content-header" }

import { computed, inject } from 'vue';
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import { useStore } from 'vuex';
import EmailLinkCompass from 'vcomponents/overview/tabs/data/EmailLinkCompass.vue';
import PhoneLinkCompass from 'vcomponents/overview/tabs/data/PhoneLinkCompass.vue';
import DrawerAccordion from 'vcomponents/common/drawer/accordion/DrawerAccordion.vue';
import { arrayToObject } from 'Helpers/arrayToObjectHelper.js';
import type { Translator } from 'js/shims-vue';

type LinkedType = 'third' | 'opportunity' | 'people';

interface Contact {
    peopleId?: number;
    fullName?: string;
    position?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    link?: RouteLocationRaw;
}

interface ContactsCountInfo {
    key: string;
    value: number;
}

interface Acl {
    update_opportunities?: boolean;
    update_contacts?: boolean;
}

interface Permissions {
    acl: Acl;
}

interface PeopleLegacy {
    edit(context: string, peopleId?: number): void;
    linkTo(type: string, options: { id: number }): void;
}

interface Props {
    linkedtype: LinkedType;
    linkedid: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OverviewContactBlock',
  props: {
    linkedtype: {},
    linkedid: {}
  },
  setup(__props: any) {

const props = __props;

const $t = inject('$t') as Translator;

const store = useStore();
const router = useRouter();
const route = useRoute();

const translations = {
    edit: $t.__('common.actions.edit'),
    disabledTooltip: $t.__('overview.contactBlock.empty.disabledTooltip'),
    seeTheFile: $t.__('common.actions.see_the_file'),
    email: $t.__('common.labels.email'),
    phone: $t.__('common.labels.phone'),
    mobile: $t.__('common.labels.mobile'),
    titlesByType: {
        third: $t.__('overview.contactBlock.third.title'),
        people: $t.__('overview.contactBlock.people.title'),
        opportunity: $t.__('overview.contactBlock.opportunity.title'),
    } satisfies Record<LinkedType, string>,
    emptyDescriptionsByType: {
        third: $t.__('overview.contactBlock.empty.third.description'),
        people: $t.__('overview.contactBlock.empty.third.description'),
        opportunity: $t.__('overview.contactBlock.empty.opportunity.description'),
    } satisfies Record<LinkedType, string>,
    emptyButtonsByType: {
        third: $t.__('overview.contactBlock.empty.third.button'),
        people: $t.__('overview.contactBlock.empty.third.button'),
        opportunity: $t.__('overview.contactBlock.empty.opportunity.button'),
    } satisfies Record<LinkedType, string>,
    allContactsLabelsByType: {
        third: $t.__('overview.contactBlock.third.allContacts'),
        people: $t.__('overview.contactBlock.third.allContacts'),
        opportunity: $t.__('overview.contactBlock.opportunity.allContacts'),
    } satisfies Record<LinkedType, string>,
};

const permissions = computed<Permissions>(() => store.getters['user/getPermissions']);

const isOpportunity = computed<boolean>(() => props.linkedtype === 'opportunity');
const isPeople = computed<boolean>(() => props.linkedtype === 'people');

const titleLabel = computed<string>(() => translations.titlesByType[props.linkedtype]);

const contact = computed<Contact>(() => {
    if (props.linkedtype === 'third') {
        return store.getters.mainContact ?? {};
    }
    if (isOpportunity.value) {
        return arrayToObject(store.getters.mainContact);
    }
    if (isPeople.value) {
        const identity = store.state.people.identity ?? {};
        const infos = arrayToObject(store.state.people.dataFetch.informations);
        return {
            peopleId: identity.id,
            fullName: identity.name,
            position: infos.position,
            email: identity.email,
            phone: infos.phone,
            mobile: identity.mobile,
            link: identity.link,
        };
    }
    return {};
});

const isEmpty = computed<boolean>(() => Object.keys(contact.value).length === 0);

const canLinkToContact = computed<boolean>(() => !isPeople.value && Boolean(contact.value.link));

const showEditButton = computed<boolean>(() => {
    const acl = permissions.value.acl;
    return Boolean(isOpportunity.value ? acl.update_opportunities : acl.update_contacts);
});

const emptyDescription = computed<string>(
    () => translations.emptyDescriptionsByType[props.linkedtype],
);

const emptyButton = computed<string>(() => translations.emptyButtonsByType[props.linkedtype]);

const isCreateButtonDisabled = computed<boolean>(() =>
    isOpportunity.value ? !permissions.value.acl.update_opportunities : false,
);

const showAllContacts = computed<boolean>(() => {
    if (isPeople.value) {
        return false;
    }
    if (isOpportunity.value) {
        return (store.state.opportunity.mainContact ?? []).length > 1;
    }
    const companyInfo: ContactsCountInfo[] = store.state.third.dataFetch.informations ?? [];
    const contactsCount = companyInfo.find((item) => item.key === 'contactsCount');
    return (contactsCount?.value ?? 0) > 1;
});

const allContactsLabel = computed<string>(
    () => translations.allContactsLabelsByType[props.linkedtype],
);

const navigate = (): void => {
    const name = isOpportunity.value
        ? 'opportunity.overview.informations'
        : 'third.overview.contacts';
    router.push({ name, query: route.query });
};

const handleEdit = (): void => {
    let context = 'thirdOverview';
    if (isOpportunity.value) {
        context = 'opportunityOverview';
    } else if (isPeople.value) {
        context = 'peopleOverview';
    }
    (People as PeopleLegacy).edit(context, contact.value.peopleId);
};

const handleCreateContact = (): void => {
    if (isOpportunity.value) {
        store.dispatch('popupPanel/open', {
            key: 'opportunityForm',
            props: {
                oppId: props.linkedid,
                emitEditSuccess: 'opportunity:update',
                context: 'overview',
            },
        });
        return;
    }
    (People as PeopleLegacy).linkTo('third', { id: props.linkedid });
};

return (_ctx: any,_cache: any) => {
  const _component_CompassPlainButton = _resolveComponent("CompassPlainButton")!
  const _component_CompassObject = _resolveComponent("CompassObject")!
  const _component_CompassButton = _resolveComponent("CompassButton")!
  const _component_CompassTooltip = _resolveComponent("CompassTooltip")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_CompassDescriptionListItem = _resolveComponent("CompassDescriptionListItem")!
  const _component_CompassDescriptionList = _resolveComponent("CompassDescriptionList")!

  return (_openBlock(), _createBlock(DrawerAccordion, {
    id: "contact",
    title: titleLabel.value,
    coloredIcon: "user-circle",
    openByDefault: ""
  }, {
    action: _withCtx(() => [
      (showEditButton.value && !isEmpty.value)
        ? (_openBlock(), _createBlock(_component_CompassPlainButton, {
            key: 0,
            label: translations.edit,
            size: _ctx.CompassEnums.PlainButtonSize.SMALL,
            variant: _ctx.CompassEnums.PlainButtonVariantType.NEUTRAL,
            onClick: _withModifiers(handleEdit, ["stop"])
          }, null, 8 /* PROPS */, ["label", "size", "variant"]))
        : _createCommentVNode("v-if", true)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (isEmpty.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_component_CompassObject, {
                  size: _ctx.CompassEnums.AvatarSize.SMALL,
                  object: "contact"
                }, null, 8 /* PROPS */, ["size"]),
                _createElementVNode("p", _hoisted_4, _toDisplayString(emptyDescription.value), 1 /* TEXT */),
                _createVNode(_component_CompassTooltip, {
                  content: translations.disabledTooltip,
                  disabled: !isCreateButtonDisabled.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassButton, {
                      type: _ctx.CompassEnums.ButtonType.SECONDARY,
                      size: _ctx.CompassEnums.ButtonSize.SMALL,
                      label: emptyButton.value,
                      class: "overview-contact-block__empty-button",
                      disabled: isCreateButtonDisabled.value,
                      onClick: handleCreateContact
                    }, null, 8 /* PROPS */, ["type", "size", "label", "disabled"])
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["content", "disabled"])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  (canLinkToContact.value)
                    ? (_openBlock(), _createBlock(_component_CompassTooltip, {
                        key: 0,
                        content: translations.seeTheFile,
                        arrow: false,
                        placement: "right-start"
                      }, {
                        default: _withCtx(() => [
                          (contact.value.link)
                            ? (_openBlock(), _createBlock(_component_RouterLink, {
                                key: 0,
                                to: contact.value.link
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_CompassObject, {
                                    label: contact.value.fullName,
                                    caption: contact.value.position,
                                    size: _ctx.CompassEnums.AvatarSize.SMALL,
                                    object: "contact",
                                    isLink: ""
                                  }, null, 8 /* PROPS */, ["label", "caption", "size"])
                                ]),
                                _: 1 /* STABLE */
                              }, 8 /* PROPS */, ["to"]))
                            : _createCommentVNode("v-if", true)
                        ]),
                        _: 1 /* STABLE */
                      }, 8 /* PROPS */, ["content"]))
                    : (_openBlock(), _createBlock(_component_CompassObject, {
                        key: 1,
                        label: contact.value.fullName,
                        caption: contact.value.position,
                        size: _ctx.CompassEnums.AvatarSize.SMALL,
                        object: "contact"
                      }, null, 8 /* PROPS */, ["label", "caption", "size"]))
                ]),
                _createVNode(_component_CompassDescriptionList, {
                  variant: _ctx.CompassEnums.DescriptionListVariantType.FROZEN_FIELD,
                  size: _ctx.CompassEnums.DescriptionListSize.SMALL
                }, {
                  default: _withCtx(() => [
                    (contact.value.email)
                      ? (_openBlock(), _createBlock(_component_CompassDescriptionListItem, {
                          key: 0,
                          label: translations.email
                        }, {
                          default: _withCtx(() => [
                            _createVNode(EmailLinkCompass, {
                              email: contact.value.email,
                              linkedid: __props.linkedid,
                              linkedtype: __props.linkedtype
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(contact.value.email), 1 /* TEXT */)
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["email", "linkedid", "linkedtype"])
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["label"]))
                      : _createCommentVNode("v-if", true),
                    (contact.value.phone)
                      ? (_openBlock(), _createBlock(_component_CompassDescriptionListItem, {
                          key: 1,
                          label: translations.phone
                        }, {
                          default: _withCtx(() => [
                            _createVNode(PhoneLinkCompass, {
                              phoneNumber: contact.value.phone
                            }, null, 8 /* PROPS */, ["phoneNumber"])
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["label"]))
                      : _createCommentVNode("v-if", true),
                    (contact.value.mobile)
                      ? (_openBlock(), _createBlock(_component_CompassDescriptionListItem, {
                          key: 2,
                          label: translations.mobile
                        }, {
                          default: _withCtx(() => [
                            _createVNode(PhoneLinkCompass, {
                              phoneNumber: contact.value.mobile
                            }, null, 8 /* PROPS */, ["phoneNumber"])
                          ]),
                          _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["label"]))
                      : _createCommentVNode("v-if", true)
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["variant", "size"])
              ]))
        ]),
        (showAllContacts.value)
          ? (_openBlock(), _createBlock(_component_CompassPlainButton, {
              key: 0,
              class: "overview-contact-block__all-contacts",
              label: allContactsLabel.value,
              onClick: navigate
            }, null, 8 /* PROPS */, ["label"]))
          : _createCommentVNode("v-if", true)
      ])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["title"]))
}
}

})